/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorBaseComponent" />
import * as React from "react";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { InputValidationActionCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/InputValidationActionCreator";
import { InputValidationStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/InputValidationStore";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { InputDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";

export interface IEndpointEditorBaseComponentProps extends IEndpointEditorComponentProps {
    instanceId: string;
}

export interface IEndpointEditorBaseComponentState {
}

export abstract class EndpointEditorBaseComponent<P extends IEndpointEditorBaseComponentProps, S extends IEndpointEditorBaseComponentState> extends VssComponent<P, S>{
    constructor(props: P, context: IVssComponentContext) {
        super(props, context);
        this._errorDictionary = {};
        this._validationActionCreator = ActionCreatorManager.CreateActionCreator<InputValidationActionCreator, IVssPageContext>
            (InputValidationActionCreator, props.instanceId, context.pageContext);
        this._validationStore = StoreManager.GetStore<InputValidationStore>(InputValidationStore, props.instanceId);

        this._validationStore.addChangedListener(this._onInputValidationStoreChange);
        this._isValid = true;
        this._isDirty = this.props.isUpdateForm ? false : undefined;
    }

    public componentWillUnmount(): void {
        this._validationStore.removeChangedListener(this._onInputValidationStoreChange);
        StoreManager.DeleteStore<InputValidationStore>(InputValidationStore, this.props.instanceId);
    }

    protected addInputs = (inputDetailsList: InputDetails[]) => {
        setTimeout(() => {
            this._validationActionCreator.addInputs(inputDetailsList);
        });
    }

    protected removeInputs = (inputIds: string[]) => {
        setTimeout(() => {
            this._validationActionCreator.removeInputs(inputIds);
        });
    }

    protected validateInputState = (inputId: string, value: any) => {
        this._validationActionCreator.validateInputState(inputId, value, this.props.isUpdateForm);
    }

    protected setInputError = (inputId: string, errorMessage: string) => {
        this._validationActionCreator.setInputError(inputId, errorMessage);
    }

    protected getInputError = (inputId: string) => {
        const errorMessage = this._errorDictionary[inputId];
        return (!errorMessage || errorMessage === "DefaultRequiredError") ? "" : errorMessage;

    }

    protected abstract handleValidAndDirtyStateChange(isValid?: boolean, isDirty?: boolean): void;

    private _onInputValidationStoreChange = (): void => {
        const storeState = this._validationStore.getState();

        const hasIsValidChanged = this._isValid !== storeState.isValid;
        const hasIsDirtyChanged = this.props.isUpdateForm && this._isDirty !== storeState.isDirty;

        if (hasIsValidChanged || hasIsDirtyChanged) {
            this._isValid = storeState.isValid;
            this._isDirty = storeState.isDirty;
            this.handleValidAndDirtyStateChange(
                hasIsValidChanged ? this._isValid : undefined,
                hasIsDirtyChanged ? this._isDirty : undefined);
        }

        this._errorDictionary = storeState.errorDictionary;
    }

    private _isValid: boolean;
    private _isDirty: boolean;
    private _errorDictionary: { [inputId: string]: string };
    private _validationStore: InputValidationStore;
    private _validationActionCreator: InputValidationActionCreator;
}
